import { ActionUpdateField } from 'app/automations/automation-helpers-actions';
import { AutomationEditorConfig, TypeStep } from 'app/automations/automations-models';
import { ContactMode } from './contact-mode';
import { UPDATE_SAFE_ADD_CONTACT_ACTIONS } from './update-safe-add-contact-actions';

const UPDATE_GROUPS: ReadonlyArray<string> = [TypeStep.FieldMapping, TypeStep.UpdateOtherFields];

function addContactActionTypes(config: AutomationEditorConfig): string[] {
  const addGroup = config.actionsConfig?.find((group) => group.nameGroup === TypeStep.AddContact);
  return (addGroup?.actionsGroup ?? []).map((item) => item.action?.$type).filter((type) => !!type);
}

function createActionTypes(config: AutomationEditorConfig): string[] {
  return addContactActionTypes(config).filter(
    (type) =>
      !(ActionUpdateField as readonly string[]).includes(type) && !UPDATE_SAFE_ADD_CONTACT_ACTIONS.includes(type),
  );
}

function triggerActionTypes(config: AutomationEditorConfig): string[] {
  const triggerGroups = config.actionsConfig?.filter((group) => group.nameGroup === TypeStep.Trigger) ?? [];
  return triggerGroups
    .flatMap((group) => group.actionsGroup ?? [])
    .map((item) => item.action?.$type)
    .filter((type) => !!type);
}

function updateActionTypes(config: AutomationEditorConfig): string[] {
  const groups = config.actionsConfig?.filter((group) => UPDATE_GROUPS.includes(group.nameGroup)) ?? [];
  const fieldUpdates = groups
    .flatMap((group) => group.actionsGroup ?? [])
    .map((item) => item.action?.$type)
    .filter((type) => type && (ActionUpdateField as readonly string[]).includes(type));
  // A consent-only Update save persists just this action, so it is the only evidence of Update mode.
  const updateSafeAddContact = addContactActionTypes(config).filter((type) =>
    UPDATE_SAFE_ADD_CONTACT_ACTIONS.includes(type),
  );
  // A trigger resolves an existing contact rather than creating one.
  return [...fieldUpdates, ...updateSafeAddContact, ...triggerActionTypes(config)];
}

// Update actions but no create action means "update an existing contact"; everything else (including a
// fresh, unconfigured integration) defaults to Add.
export function inferContactMode(
  configs: AutomationEditorConfig[],
  appName: string,
  savedActionTypes: string[],
): ContactMode {
  const config = configs.find((item) => item.name === appName);
  if (!config) {
    return ContactMode.Add;
  }
  const createTypes = createActionTypes(config);
  const updateTypes = updateActionTypes(config);
  const hasCreate = savedActionTypes.some((type) => createTypes.includes(type));
  const hasUpdate = savedActionTypes.some((type) => updateTypes.includes(type));
  return hasUpdate && !hasCreate ? ContactMode.Update : ContactMode.Add;
}
